import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../Redux/Store';
import { addCart, fetchCart, updateCartItemQuantity } from '../Redux/Public/orderApi';
import { OrderItem } from '../types/order';

interface UseCartProps {
  activeStoreId: string;
  activeDiningTableId: string;
  userType: 'customer' | 'clerk';
}

export const useCart = ({ activeStoreId, activeDiningTableId, userType }: UseCartProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<OrderItem | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [itemToDelete, setItemToDelete] = useState<[string, string | null] | null>(null);
  // 売り切れ拒否（422、FE ADR 0054）などカート追加失敗時のユーザー向けメッセージ
  const [cartError, setCartError] = useState<string | null>(null);

  const handleAddClick = (item: OrderItem) => {
    setSelectedItem(item);
    setShowQuantityModal(true);
  };

  const handleAddToCart = async (selectedItem: OrderItem, quantity: number, menuOptionId: string | null) => {
    if (selectedItem) {
      const result = await dispatch(
        addCart({
          menuOptionId,
          activeStoreId,
          activeDiningTableId,
          menuId: selectedItem.id,
          quantity,
          userType,
        })
      );

      if (addCart.rejected.match(result)) {
        setCartError((result.payload as string) || 'カートへの追加に失敗しました。');
        return;
      }

      // カート情報を更新
      dispatch(fetchCart({
        activeStoreId,
        activeDiningTableId,
        userType
      }));
    }
  };

  const handleCartItemQuantity = async (
    menuId: string,
    menuOptionId: string | null,
    quantity: number
  ) => {
    await dispatch(
      updateCartItemQuantity({
        menuId,
        menuOptionId,
        quantity,
        activeStoreId,
        activeDiningTableId,
        userType,
      })
    );

    // カート情報を更新
    dispatch(fetchCart({ 
      activeStoreId, 
      activeDiningTableId, 
      userType 
    }));
  };

  const handleRemoveItem = (cartId: string, menuId: string, menuOptionId: string | null) => {
    setItemToDelete([menuId, menuOptionId]);
  };

  const handleConfirmDelete = async () => {
    if (itemToDelete) {
      const [menuId, menuOptionId] = itemToDelete;

      await dispatch(
        updateCartItemQuantity({
          menuOptionId,
          activeStoreId,
          activeDiningTableId,
          menuId,
          quantity: 0,
          userType,
        })
      );

      // カート情報を更新
      dispatch(fetchCart({ 
        activeStoreId, 
        activeDiningTableId, 
        userType 
      }));
    }
  };

  const handleCheckoutSuccess = () => {
    // カート情報を更新
    dispatch(fetchCart({ 
      activeStoreId, 
      activeDiningTableId, 
      userType 
    }));
  };

  return {
    showQuantityModal,
    setShowQuantityModal,
    selectedItem,
    quantity,
    setQuantity,
    handleAddClick,
    handleCartItemQuantity,
    handleRemoveItem,
    handleConfirmDelete,
    handleCheckoutSuccess,
    handleAddToCart,
    cartError,
    clearCartError: () => setCartError(null),
  };
};